import React, { useState, useEffect, useRef } from "react";
import { Button, Form, Spinner } from "@deere/ux.uxframe-react";
import { useHistory, useLocation } from "react-router-dom";
import { Option, Select, ThemeProvider, DatePicker, InputAdornment } from "@deere/fuel-react";
import Checkbox from "@mui/material/Checkbox";
import TextField from "@mui/material/TextField";
import GlobalStyles from "@mui/material/GlobalStyles";
import { useTheme } from "@mui/material/styles";
import { useDispatch, useSelector } from "react-redux";
import wtyReportApi from "../../services/warrantyReportAxios";
import ErrorToast from "../common/ErrorToast";
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  CalendarMonth,
} from "@mui/icons-material";
import { saveRegionalFilters, clearRegionalFilters, selectRegionalSavedFilters } from "./regionalDetailsSlice";
import MultiSelectDropdown from "./MultiSelectDropdown";

const RegionalDetails = () => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const theme = useTheme();
  const savedFilters = useSelector(selectRegionalSavedFilters);

  // Branches state
  const [branches, setBranches] = useState([]);
  const [branchesLoading, setBranchesLoading] = useState(false);

  // Dealer Account state
  const [branch, setBranch] = useState("");
  const [availableDealerTypes, setAvailableDealerTypes] = useState([]);
  const [territoriesLoading, setTerritoriesLoading] = useState(false);
  const [pendingDealerTypes, setPendingDealerTypes] = useState([]);
  const [dealerTypes, setDealerTypes] = useState([]);
  const [productLines, setProductLines] = useState([]);
  const [dealerAccounts, setDealerAccounts] = useState([]);
  const [dealerAccountsLoading, setDealerAccountsLoading] = useState(false);

  // Machine Details state
  const [factoryCodes, setFactoryCodes] = useState([]);
  const [factoryCodesLoading, setFactoryCodesLoading] = useState(false);
  const [pendingMachineCategory, setPendingMachineCategory] = useState([]);
  const [machineCategory, setMachineCategory] = useState([]);
  const [machineModels, setMachineModels] = useState([]);
  const [availableModels, setAvailableModels] = useState([]);
  const [modelsLoading, setModelsLoading] = useState(false);

  // onClose trigger flags
  const [territoryClosed, setTerritoryClosed] = useState(false);
  const [factoryCodeClosed, setFactoryCodeClosed] = useState(false);
  const [dealerSerial, setDealerSerial] = useState("");
  const [dealerModel, setDealerModel] = useState("");
  const [machineSerial, setMachineSerial] = useState("");
  const [machineModel, setMachineModel] = useState("");

  // PIN search state
  const [searchTerm, setSearchTerm] = useState("");
  const [validationError, setValidationError] = useState("");
  const [pinSearchLoading, setPinSearchLoading] = useState(false);
  const [filterSearchLoading, setFilterSearchLoading] = useState(false);
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [anyDatePickerOpen, setAnyDatePickerOpen] = useState(false);
  const savedScrollPos = useRef(0);

  // Date range state
  const [dateRangeType, setDateRangeType] = useState("custom");
  const [deliveryDateRange, setDeliveryDateRange] = useState(null);
  const [buildDateRange, setBuildDateRange] = useState(null);
  const [dateRangeValidationError, setDateRangeValidationError] = useState("");
  const [buildDateRangeValidationError, setBuildDateRangeValidationError] = useState("");

  // Restore filters when navigating back from DealerReport
  useEffect(() => {
    if (location.state?.restoreFilters && savedFilters) {
      setBranches(savedFilters.branches ?? []);
      setBranch(savedFilters.branch ?? "");
      setAvailableDealerTypes(savedFilters.availableDealerTypes ?? []);
      setPendingDealerTypes(savedFilters.pendingDealerTypes ?? []);
      setDealerTypes(savedFilters.dealerTypes ?? []);
      setProductLines(savedFilters.productLines ?? []);
      setDealerAccounts(savedFilters.dealerAccounts ?? []);
      setFactoryCodes(savedFilters.factoryCodes ?? []);
      setPendingMachineCategory(savedFilters.pendingMachineCategory ?? []);
      setMachineCategory(savedFilters.machineCategory ?? []);
      setAvailableModels(savedFilters.availableModels ?? []);
      setMachineModels(savedFilters.machineModels ?? []);
      setMachineSerial(savedFilters.machineSerial ?? "");
      setMachineModel(savedFilters.machineModel ?? "");
      setSearchTerm(savedFilters.searchTerm ?? "");
      setDateRangeType(savedFilters.dateRangeType ?? "custom");
      setDeliveryDateRange(
        savedFilters.deliveryDateRange
          ? savedFilters.deliveryDateRange.map((d) => (d ? new Date(d) : null))
          : null
      );
      setBuildDateRange(
        savedFilters.buildDateRange
          ? savedFilters.buildDateRange.map((d) => (d ? new Date(d) : null))
          : null
      );
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // const availableProductLines = getAvailableProductLines(dealerTypes);

  const isPinActive = searchTerm.trim().length > 0;
  const isFilterActive =
    !!branch ||
    pendingDealerTypes.length > 0 ||
    dealerTypes.length > 0 ||
    productLines.length > 0 ||
    pendingMachineCategory.length > 0 ||
    machineModels.length > 0 ||
    !!machineSerial ||
    !!machineModel ||
    (deliveryDateRange && (deliveryDateRange[0] || deliveryDateRange[1])) ||
    (buildDateRange && (buildDateRange[0] || buildDateRange[1]));

  useEffect(() => {
    setFactoryCodesLoading(true);
    setBranchesLoading(true);

    Promise.all([
      wtyReportApi.get("/mpr/factories/codes"),
      wtyReportApi.get("/mpr/branches/codes"),
    ])
      .then(([factoriesRes, branchesRes]) => {
        const factoryData = Array.isArray(factoriesRes.data) ? factoriesRes.data : [];
        setFactoryCodes(factoryData.map((item) => {
          const raw = typeof item === "string" ? item : (item.code || item.name || "");
          const code = raw.split("-")[0].trim();
          return { code, name: code };
        }));

        const branchData = Array.isArray(branchesRes.data) ? branchesRes.data : [];
        setBranches(branchData.map((b) => ({ label: b, value: b })));
      })
      .catch(() => {
        setShowErrorToast(true);
        setFactoryCodes([]);
        setBranches([]);
      })
      .finally(() => {
        setFactoryCodesLoading(false);
        setBranchesLoading(false);
      });
  }, []);

  const fetchTerritories = (selectedBranch) => {
    if (!selectedBranch) {
      setAvailableDealerTypes([]);
      return;
    }

    setTerritoriesLoading(true);
    wtyReportApi
      .get("/mpr/territories", { branch: selectedBranch })
      .then((res) => {
        const data = res.data;
        const list = Array.isArray(data)
          ? data.map((t) => ({
              value: t.servTerrCd,
              label: t.servTerrCd,
              servDivCd: t.servDivCd,
              servTerrCd: t.servTerrCd,
            }))
          : [];
        setAvailableDealerTypes(list);
      })
      .catch(() => {
        setShowErrorToast(true);
        setAvailableDealerTypes([]);
      })
      .finally(() => {
        setTerritoriesLoading(false);
      });
  };

  useEffect(() => {
    if (!territoryClosed) return;
    setTerritoryClosed(false);
    setDealerTypes(pendingDealerTypes);
    setProductLines([]);
    setDealerAccounts([]);
    if (!branch || !pendingDealerTypes.length) return;
    setDealerAccountsLoading(true);
    wtyReportApi
      .post("/mpr/dealers", { branches: [branch], territories: pendingDealerTypes })
      .then((res) => {
        const data = Array.isArray(res.data) ? res.data : [];
        setDealerAccounts(data);
      })
      .catch(() => {
        setShowErrorToast(true);
        setDealerAccounts([]);
      })
      .finally(() => {
        setDealerAccountsLoading(false);
      });
  }, [territoryClosed]);

  useEffect(() => {
    if (!factoryCodeClosed) return;
    setFactoryCodeClosed(false);
    if (!machineCategory.length) return;
    setModelsLoading(true);
    wtyReportApi
      .post("/mpr/models", { factoryCodes: machineCategory })
      .then((res) => {
        const data = Array.isArray(res.data) ? res.data : [];
        setAvailableModels(data);
      })
      .catch(() => {
        setShowErrorToast(true);
        setAvailableModels([]);
      })
      .finally(() => {
        setModelsLoading(false);
      });
  }, [factoryCodeClosed, machineCategory]);

  const isDealerTypeDirty = pendingDealerTypes.slice().sort().join() !== dealerTypes.slice().sort().join();
  const isMachineCategoryDirty = pendingMachineCategory.slice().sort().join() !== machineCategory.slice().sort().join();

  const handleBranchChange = (e) => {
    const newBranch = e.target.value;
    setBranch(newBranch);
    setPendingDealerTypes([]);
    setDealerTypes([]);
    setProductLines([]);
    setDealerAccounts([]);
    setAvailableDealerTypes([]);
    fetchTerritories(newBranch);
  };

  const handleDealerTypeChange = (selected) => {
    setPendingDealerTypes(selected);
  };

  const applyDealerTypes = () => {
    if (!isDealerTypeDirty) return;
    setTerritoryClosed(true);
  };

  const handleProductLineChange = (selected) => {
    setProductLines(selected);
  };

  const handleMachineCategoryChange = (selected) => {
    setPendingMachineCategory(selected);
  };

  const handleMachineCategoryClose = () => {
    if (!isMachineCategoryDirty) return;
    setMachineCategory(pendingMachineCategory);
    setMachineModels([]);
    setAvailableModels([]);
    setFactoryCodeClosed(true);
  };

  const handleMachineModelChange = (selected) => {
    setMachineModels(selected);
  };

  const validateSearchInput = (input) => {
    const trimmed = input.trim();
    
    if (trimmed.length !== 13 && trimmed.length !== 17) {
      return "PIN must be exactly 13 or 17 characters long.";
    }
    
    return "";
  };

  const handleSearchInputChange = (event) => {
    const value = event.target.value;
    setSearchTerm(value);
    
    if (value.trim().length > 0) {
      const error = validateSearchInput(value);
      setValidationError(error);
    } else {
      setValidationError("");
    }
  };

  const handleSearch = async (event) => {
    event.preventDefault();
    const trimmedSearch = searchTerm.trim();

    if (trimmedSearch.length > 0) {
      const error = validateSearchInput(trimmedSearch);
      if (error) {
        setValidationError(error);
        return;
      }
      // PIN search — GET API
      setPinSearchLoading(true);
      saveFiltersToRedux();
      try {
        const res = await wtyReportApi.get("/mpr/search/pin", { pin: trimmedSearch });
        history.push({
          pathname: "/dealer/report",
          state: {
            pin: trimmedSearch,
            searchResults: Array.isArray(res.data) ? res.data : [],
            searchCriteria: { pin: trimmedSearch },
          },
        });
      } catch {
        setShowErrorToast(true);
        history.push({
          pathname: "/dealer/report",
          state: { pin: trimmedSearch, searchResults: [] },
        });
      } finally {
        setPinSearchLoading(false);
      }
      return;
    }

    const deliveryError = validateDateRange(deliveryDateRange);
    const buildError = validateDateRange(buildDateRange);

    if (deliveryError || buildError) {
      setDateRangeValidationError(deliveryError);
      setBuildDateRangeValidationError(buildError);
      return;
    }

    const formatDate = (date) => {
      if (!date) return "";
      return new Date(date).toISOString().split("T")[0];
    };

    const requestBody = {
      branches: branch ? [branch] : [],
      serviceTerritories: dealerTypes,
      dealerAccounts: productLines,
      factoryCodes: machineCategory,
      models: machineModels,
      serialNumberFrom: machineSerial,
      serialNumberTo: machineModel,
      deliveryDateFrom: formatDate(deliveryDateRange?.[0]),
      deliveryDateTo: formatDate(deliveryDateRange?.[1]),
      buildDateFrom: formatDate(buildDateRange?.[0]),
      buildDateTo: formatDate(buildDateRange?.[1]),
      pin: "",
    };

    setFilterSearchLoading(true);
    saveFiltersToRedux();
    try {
      const res = await wtyReportApi.post("/mpr/search", requestBody);
      history.push({
        pathname: "/dealer/report",
        state: {
          searchResults: Array.isArray(res.data) ? res.data : [],
          searchCriteria: requestBody,
        },
      });
    } catch {
      setShowErrorToast(true);
      history.push({
        pathname: "/dealer/report",
        state: { searchResults: [] },
      });
    } finally {
      setFilterSearchLoading(false);
    }
  };

  const validateDateRange = (range) => {
    if (!range || !range[0] || !range[1]) {
      return "";
    }
    const startDate = new Date(range[0]);
    const endDate = new Date(range[1]);
    if (endDate <= startDate) {
      return "End date must be greater than start date.";
    }
    return "";
  };

  const normalizeToDateOnly = (value) => {
    if (!value) {
      return null;
    }
    const date = new Date(value);
    date.setHours(0, 0, 0, 0);
    return date;
  };

  const getExpectedRollingRange = (rangeType) => {
    if (rangeType !== "rolling12" && rangeType !== "rolling24") {
      return null;
    }
    const endDate = normalizeToDateOnly(new Date());
    const startDate = new Date(endDate);
    if (rangeType === "rolling12") {
      startDate.setFullYear(startDate.getFullYear() - 1);
    } else {
      startDate.setFullYear(startDate.getFullYear() - 2);
    }
    return [startDate, endDate];
  };

  const doesRangeMatchSelectedRollingType = (range, selectedRangeType) => {
    const expectedRange = getExpectedRollingRange(selectedRangeType);
    if (!expectedRange) {
      return true;
    }
    const normalizedStart = normalizeToDateOnly(range?.[0]);
    const normalizedEnd = normalizeToDateOnly(range?.[1]);
    if (!normalizedStart || !normalizedEnd) {
      return false;
    }
    const [expectedStart, expectedEnd] = expectedRange;
    return normalizedStart.getTime() === expectedStart.getTime() && normalizedEnd.getTime() === expectedEnd.getTime();
  };

  const handleDeliveryStartDateChange = (dates) => {
    const startDate = Array.isArray(dates) ? dates[0] || null : dates || null;
    const endDate = deliveryDateRange?.[1] || null;
    const range = [startDate, endDate];
    setDeliveryDateRange(range);
    setDateRangeValidationError(validateDateRange(range));
    if (!doesRangeMatchSelectedRollingType(range, dateRangeType)) {
      setDateRangeType("custom");
    }
  };

  const handleDeliveryEndDateChange = (dates) => {
    const endDate = Array.isArray(dates) ? dates[0] || null : dates || null;
    const startDate = deliveryDateRange?.[0] || null;
    const range = [startDate, endDate];
    setDeliveryDateRange(range);
    setDateRangeValidationError(validateDateRange(range));
    if (!doesRangeMatchSelectedRollingType(range, dateRangeType)) {
      setDateRangeType("custom");
    }
  };

  const handleBuildStartDateChange = (dates) => {
    const startDate = Array.isArray(dates) ? dates[0] || null : dates || null;
    const endDate = buildDateRange?.[1] || null;
    const range = [startDate, endDate];
    setBuildDateRange(range);
    setBuildDateRangeValidationError(validateDateRange(range));
  };

  const handleBuildEndDateChange = (dates) => {
    const endDate = Array.isArray(dates) ? dates[0] || null : dates || null;
    const startDate = buildDateRange?.[0] || null;
    const range = [startDate, endDate];
    setBuildDateRange(range);
    setBuildDateRangeValidationError(validateDateRange(range));
  };

  const handleResetDateRange = () => {
    // PIN search
    setSearchTerm("");
    setValidationError("");

    // Dealer Account
    setBranch("");
    setPendingDealerTypes([]);
    setDealerTypes([]);
    setProductLines([]);
    setDealerAccounts([]);

    // Machine Details
    setPendingMachineCategory([]);
    setMachineCategory([]);
    setMachineModels([]);
    setAvailableModels([]);
    setMachineSerial("");
    setMachineModel("");

    // Date Range
    setDeliveryDateRange(null);
    setBuildDateRange(null);
    setDateRangeType("custom");
    setDateRangeValidationError("");
    setBuildDateRangeValidationError("");

    dispatch(clearRegionalFilters());
  };

  const saveFiltersToRedux = () => {
    dispatch(saveRegionalFilters({
      branches,
      branch,
      availableDealerTypes,
      pendingDealerTypes,
      dealerTypes,
      productLines,
      dealerAccounts,
      factoryCodes,
      pendingMachineCategory,
      machineCategory,
      availableModels,
      machineModels,
      machineSerial,
      machineModel,
      searchTerm,
      dateRangeType,
      deliveryDateRange: deliveryDateRange
        ? deliveryDateRange.map((d) => (d ? d.toISOString() : null))
        : null,
      buildDateRange: buildDateRange
        ? buildDateRange.map((d) => (d ? d.toISOString() : null))
        : null,
    }));
  };

  const handleDateRangeTypeChange = (rangeType) => {
    setDateRangeType(rangeType);
    setDateRangeValidationError("");
    setBuildDateRangeValidationError("");
    
    if (rangeType === "rolling12") {
      const today = new Date();
      const startDate = new Date(today);
      startDate.setFullYear(startDate.getFullYear() - 1);
      setDeliveryDateRange([startDate, today]);
    } else if (rangeType === "rolling24") {
      const today = new Date();
      const startDate = new Date(today);
      startDate.setFullYear(startDate.getFullYear() - 2);
      setDeliveryDateRange([startDate, today]);
    } else if (rangeType === "custom") {
      setDeliveryDateRange(null);
    }
  };

  const roundedControlStyle = { borderRadius: "5px" };
  const subTitleColor = theme.palette.text.primary;
  const greyColor = theme.palette.text.secondary;
  const isAnyApiLoading = branchesLoading || factoryCodesLoading || territoriesLoading || dealerAccountsLoading || modelsLoading;

  return (
    <ThemeProvider>
      {showErrorToast && <ErrorToast onClose={() => setShowErrorToast(false)} />}
      <GlobalStyles
        styles={{
          'button[role="combobox"]': { width: "100%", boxSizing: "border-box" },
          "#branch-dropdown, #service-territory-dropdown, #dealer-code-dropdown, #factory-code-dropdown, #machine-model-dropdown":
            { width: "220px", boxSizing: "border-box" },
          ".find-dealer-btn:hover": {
            textDecoration: "underline",
            opacity: "1 !important",
          },
        }}
      />
      {(pinSearchLoading || filterSearchLoading || isAnyApiLoading) && (
        <div className="d-flex w-100 justify-content-center align-items-center" style={{ position: "fixed", inset: 0, backgroundColor: "rgba(255,255,255,0.6)", zIndex: 9999 }}>
          <Spinner
            accessibilityLabel="Loading"
            className="uxf-spinner-border-lg"
            animation="border"
          />
        </div>
      )}
      <div style={{ padding: "24px", overflowX: "auto"}}>
        {/* PIN Search Section */}
        <p style={{ marginBottom: "12px", color: "#C7C6C5" }}>
          Regional analysis across branches, territories, and dealer networks.
        </p>

        <div>
          <Form
            onSubmit={handleSearch}
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: "12px",
              width: "100%",
            }}
          >
            <div style={{ position: "relative", width: "285px" }}>
              <SearchIcon style={{ fontSize: "20px", position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }} />
              <Form.Control
                value={searchTerm}
                onChange={handleSearchInputChange}
                placeholder="Search PIN"
                aria-label="Regional details search"
                disabled={!!isFilterActive}
                maxLength={17}
                style={{ paddingLeft: "40px", minHeight: "36px", borderRadius: "5px", opacity: isFilterActive ? 0.5 : 1 }}
              />
            </div>

            <Button
              variant="primary"
              type="submit"
              disabled={!isPinActive || !!validationError || !!isFilterActive || pinSearchLoading || isAnyApiLoading}
              style={{ marginTop: "0", borderRadius: "5px" }}
            >
              {pinSearchLoading ? "Searching..." : "Search"}
            </Button>

            <Button
              variant="outline"
              onClick={handleResetDateRange}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                border: "1px solid #555",
                borderRadius: "5px",
                marginTop: "0",
                marginLeft: "auto",
                backgroundColor: "#ffffff",
              }}
            >
              <RefreshIcon style={{ fontSize: "18px" }} />
              Reset
            </Button>
          </Form>

          <div style={{ marginTop: "8px", paddingLeft: "0" }}>
            {validationError && (
              <div style={{ color: "red", fontSize: "12px" }}>
                {validationError}
              </div>
            )}
            {!validationError && searchTerm.trim().length > 0 && (
              <div style={{ fontSize: "12px" }}>
                <span style={{ fontWeight: "bold", color: "black" }}>
                  {searchTerm.trim().length} digit PIN Searching:
                </span>
                {" "}
                <span style={{ fontWeight: "normal", color: "#555" }}>
                  {searchTerm.trim()}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Dealer Account Section */}
        <div style={{ marginTop: "0" }}>
          <hr style={{ margin: "15px 0", border: "none", borderTop: "1px solid #E0E0E0" }} />
          <h5 style={{ marginBottom: "14px", fontWeight: 600, fontSize: "16px" }}>Dealer Account</h5>

          <div style={{ display: "flex", gap: "24px", flexWrap: "wrap", marginBottom: "24px" }}>
            <div style={{ width: "220px" }}>
              <label style={{ display: "block", marginBottom: "4px", fontWeight: 600, color: subTitleColor }}>Branch</label>
              <Select
                id="branch"
                value={branch}
                onChange={handleBranchChange}
                placeholder={branchesLoading ? "Loading..." : "Select a branch"}
                disabled={isPinActive || branchesLoading}
              >
                {branches.map((r) => (
                  <Option key={r.value} value={r.value}>
                    {r.label}
                  </Option>
                ))}
              </Select>
            </div>

            <div style={{ width: "220px" }}>
              <label style={{ display: "block", marginBottom: "4px", fontWeight: 600, color: subTitleColor }}>Service Territory</label>
              <MultiSelectDropdown
                id="service-territory"
                options={availableDealerTypes.map((d) => ({ value: d.value, label: d.label }))}
                value={pendingDealerTypes}
                onChange={handleDealerTypeChange}
                onClose={applyDealerTypes}
                placeholder={territoriesLoading ? "Loading..." : "Select dealer types"}
                disabled={isPinActive || !branch || territoriesLoading}
              />
            </div>

            <div style={{ width: "220px" }}>
              <label style={{ display: "block", marginBottom: "4px", fontWeight: 600, color: subTitleColor }}>Dealer Code</label>
              <MultiSelectDropdown
                id="dealer-code"
                options={dealerAccounts.map((a) => ({ value: a.acctId, label: `${a.acctId} - ${a.acctNm}`, tooltip: `${a.acctId} - ${a.acctNm}` }))}
                value={productLines}
                onChange={handleProductLineChange}
                placeholder={dealerAccountsLoading ? "Loading..." : "Select dealer codes"}
                disabled={isPinActive || !dealerTypes.length || dealerAccountsLoading}
                searchable
              />
            </div>
          </div>
        </div>

        {/* Machine Details Section */}
        <div style={{ marginTop: "0" }}>
          <hr style={{ margin: "15px 0", border: "none", borderTop: "1px solid #E0E0E0" }} />
          <h5 style={{ marginBottom: "14px", fontWeight: 600, fontSize: "16px" }}>Machine Details</h5>

          {/* Machine controls row */}
          <div style={{ display: "flex", gap: "24px", flexWrap: "wrap", marginBottom: "16px" }}>
            <div style={{ width: "220px" }}>
              <label style={{ display: "block", marginBottom: "4px", fontWeight: 600, color: subTitleColor }}>Factory Code</label>
              <MultiSelectDropdown
                id="factory-code"
                options={factoryCodes.map((c) => ({ value: c.code, label: c.code }))}
                value={pendingMachineCategory}
                onChange={handleMachineCategoryChange}
                onClose={handleMachineCategoryClose}
                placeholder={factoryCodesLoading ? "Loading..." : "Select factory codes"}
                disabled={isPinActive || factoryCodesLoading}
              />
            </div>

            <div style={{ width: "220px" }}>
              <label style={{ display: "block", marginBottom: "4px", fontWeight: 600, color: subTitleColor }}>Models</label>
              <MultiSelectDropdown
                id="machine-model"
                options={availableModels.map((m) => ({ value: m, label: m }))}
                value={machineModels}
                onChange={handleMachineModelChange}
                placeholder={modelsLoading ? "Loading..." : "Select models"}
                disabled={isPinActive || !machineCategory.length || modelsLoading}
                searchable
              />
            </div>

            <div style={{ minWidth: "480px" }}>
              <div style={{ marginBottom: "4px", fontWeight: 600, color: subTitleColor }}>Enter Serial Number</div>
              <div style={{ display: "flex", gap: "24px", flexWrap: "wrap" }}>
                <div style={{ width: "280px", display: "flex", alignItems: "center", gap: "10px" }}>
                  <label style={{ margin: 0, minWidth: "42px", fontWeight: 600, color: greyColor }}>From:</label>
                  <TextField
                    variant="outlined"
                    size="small"
                    value={machineSerial}
                    onChange={(e) => setMachineSerial(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    inputProps={{ maxLength: 6 }}
                    placeholder="Enter first 6 digits"
                    disabled={isPinActive}
                    style={{ width: "100%" }}
                    sx={{ "& .MuiOutlinedInput-root": { height: "36px !important" } }}
                  />
                </div>

                <div style={{ width: "280px", display: "flex", alignItems: "center", gap: "10px" }}>
                  <label style={{ margin: 0, minWidth: "42px", fontWeight: 600, color: greyColor }}>To:</label>
                  <TextField
                    variant="outlined"
                    size="small"
                    value={machineModel}
                    onChange={(e) => setMachineModel(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    inputProps={{ maxLength: 6 }}
                    placeholder="Enter last 6 digits"
                    disabled={isPinActive}
                    style={{ width: "100%" }}
                    sx={{ "& .MuiOutlinedInput-root": { height: "36px" } }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Date Range Section */}
        <div style={{ marginTop: "0" }}>
          <hr style={{ margin: "15px 0", border: "none", borderTop: "1px solid #E0E0E0" }} />
          <h5 style={{ marginTop: 0, marginBottom: "22px", fontSize: "16px", fontWeight: 600 }}>Date Range</h5>

          <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>

            <div style={{ display: "flex", gap: "40px", flexWrap: "nowrap" }}>
              {(dateRangeType === "custom" || dateRangeType === "rolling12" || dateRangeType === "rolling24") && (
                <div style={{ flex: "0 0 auto" }}>
                  <Form.Label className="uxf-label" style={{ marginBottom: "8px", display: "block", color: subTitleColor }}>Delivery Date Range</Form.Label>
                  <div style={{ display: "flex", gap: "16px" }}>
                    <div style={{ width: "308px" }}>
                    <DatePicker
                      value={deliveryDateRange?.[0] || null}
                      onChange={handleDeliveryStartDateChange}
                      onOpen={() => { savedScrollPos.current = window.scrollY; setAnyDatePickerOpen(true); setTimeout(() => window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" }), 50); }}
                      onClose={() => { setAnyDatePickerOpen(false); window.scrollTo({ top: savedScrollPos.current, behavior: "smooth" }); }}
                      placeholder="Select Start Date"
                      disabled={isPinActive}
                      flatpickrOptions={{ static: false, position: "above" }}
                      InputProps={{
                        className: "regional-date-input",
                        endAdornment: (
                          <InputAdornment position="end">
                            <CalendarMonth />
                          </InputAdornment>
                        ),
                        style: { borderRadius: "5px", overflow: "hidden", width: "308px" },
                      }}
                    />
                    </div>
                    <div style={{ width: "308px" }}>
                    <DatePicker
                      value={deliveryDateRange?.[1] || null}
                      onChange={handleDeliveryEndDateChange}
                      onOpen={() => { savedScrollPos.current = window.scrollY; setAnyDatePickerOpen(true); setTimeout(() => window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" }), 50); }}
                      onClose={() => { setAnyDatePickerOpen(false); window.scrollTo({ top: savedScrollPos.current, behavior: "smooth" }); }}
                      placeholder="Select End Date"
                      disabled={isPinActive}
                      flatpickrOptions={{ static: false, position: "above" }}
                      InputProps={{
                        className: "regional-date-input",
                        endAdornment: (
                          <InputAdornment position="end">
                            <CalendarMonth />
                          </InputAdornment>
                        ),
                        style: { borderRadius: "5px", overflow: "hidden", width: "308px" },
                      }}
                    />
                    </div>
                  </div>
                </div>
              )}

              <div style={{ flex: "0 0 auto" }}>
                <Form.Label className="uxf-label" style={{ marginBottom: "8px", display: "block", color: subTitleColor }}>Build Date Range</Form.Label>
                <div style={{ display: "flex", gap: "16px" }}>
                  <div style={{ width: "308px" }}>
                  <DatePicker
                    value={buildDateRange?.[0] || null}
                    onChange={handleBuildStartDateChange}
                    onOpen={() => { savedScrollPos.current = window.scrollY; setAnyDatePickerOpen(true); setTimeout(() => window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" }), 50); }}
                    onClose={() => { setAnyDatePickerOpen(false); window.scrollTo({ top: savedScrollPos.current, behavior: "smooth" }); }}
                    placeholder="Select Start Date"
                    disabled={isPinActive}
                    flatpickrOptions={{ static: false, position: "above" }}
                    InputProps={{
                      className: "regional-date-input",
                      endAdornment: (
                        <InputAdornment position="end">
                          <CalendarMonth />
                        </InputAdornment>
                      ),
                      style: { borderRadius: "5px", overflow: "hidden", width: "308px" },
                    }}
                  />
                  </div>
                  <div style={{ width: "308px" }}>
                  <DatePicker
                    value={buildDateRange?.[1] || null}
                    onChange={handleBuildEndDateChange}
                    onOpen={() => { savedScrollPos.current = window.scrollY; setAnyDatePickerOpen(true); setTimeout(() => window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" }), 50); }}
                    onClose={() => { setAnyDatePickerOpen(false); window.scrollTo({ top: savedScrollPos.current, behavior: "smooth" }); }}
                    placeholder="Select End Date"
                    disabled={isPinActive}
                    flatpickrOptions={{ static: false, position: "above" }}
                    InputProps={{
                      className: "regional-date-input",
                      endAdornment: (
                        <InputAdornment position="end">
                          <CalendarMonth />
                        </InputAdornment>
                      ),
                      style: { borderRadius: "5px", overflow: "hidden", width: "308px" },
                    }}
                  />
                  </div>
                </div>
                {buildDateRangeValidationError && (
                  <div style={{ color: "red", fontSize: "12px", marginTop: "8px" }}>
                    {buildDateRangeValidationError}
                  </div>
                )}
              </div>
            </div>

            <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end", alignItems: "end", width: "100%" }}>
              <Button variant="primary" onClick={handleSearch} disabled={isPinActive || !isFilterActive || filterSearchLoading || isAnyApiLoading} style={roundedControlStyle}>
                {filterSearchLoading ? "Searching..." : "Search"}
              </Button>
            </div>
          </div>
        </div>

        <style>
          {`.regional-date-input,
            .regional-date-input fieldset,
            .regional-date-input .MuiOutlinedInput-notchedOutline {
              border-radius: 5px !important;
            }`}
        </style>
      </div>
    </ThemeProvider>
  );
};

export default RegionalDetails;
